import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, KeyRound, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import axios from 'src/common/axiosInstance';
import './ApiKeysPage.css';

const displayDate = (value, empty = 'Never') => value ? new Date(value).toLocaleString() : empty;
const errorMessage = (error) => Object.values(error.response?.data?.errors || {}).flat().join(' ') || error.response?.data?.message || 'Unable to reach Auto Appy. Refresh the list before trying again.';

export default function ApiKeysPage({ application = 'main', appName = 'Auto Appy', endpoint = '/api/v1/api-keys' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [permission, setPermission] = useState('read');
  const [expiry, setExpiry] = useState('');
  const [created, setCreated] = useState(null);
  const [revoke, setRevoke] = useState(null);
  const [notice, setNotice] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const nameInput = useRef(null);
  const secretInput = useRef(null);
  const createButton = useRef(null);
  const revokeSection = useRef(null);
  const mounted = useRef(true);
  const wasCreating = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(endpoint, { params: { application } });
      if (mounted.current) { setData(response.data.data); setUncertain(false); }
    } catch (failure) {
      if (mounted.current) setError(errorMessage(failure));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [application, endpoint]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (creating) nameInput.current?.focus(); }, [creating]);
  useEffect(() => { if (created) secretInput.current?.focus(); }, [created]);

  useEffect(() => {
    if (revoke) {
      revokeSection.current?.scrollIntoView?.({ block: 'center' });
      revokeSection.current?.focus();
    }
  }, [revoke]);

  useEffect(() => {
    const open = Boolean(creating || created);
    if (wasCreating.current && !open) createButton.current?.focus();
    wasCreating.current = open;
  }, [creating, created]);

  const closeSecret = () => { setCreated(null); setNotice(''); createButton.current?.focus(); };
  const submit = async (event) => {
    event.preventDefault();
    if (busy || uncertain || !name.trim()) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const response = await axios.post(endpoint, {
        application, name: name.trim(), permission, expires_in_days: expiry ? Number(expiry) : null,
      });
      if (!mounted.current) return;
      setCreated(response.data.data);
      setCreating(false); setName('');
      await refresh();
    } catch (failure) {
      if (mounted.current) {
        setError(errorMessage(failure));
        if (!failure.response || failure.response.status >= 500) setUncertain(true);
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const revokeKey = async () => {
    if (!revoke || busy) return;
    setBusy(true); setError('');
    try {
      await axios.delete(endpoint + '/' + encodeURIComponent(revoke.id), { params: { application } });
      if (!mounted.current) return;
      if (created?.key?.id === revoke.id) setCreated(null);
      setRevoke(null);
      setNotice('API key revoked. New requests using it will be rejected.');
      await refresh();
    } catch (failure) {
      if (mounted.current) setError(errorMessage(failure));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.secret);
      setNotice('API key copied.');
    } catch {
      secretInput.current?.focus(); secretInput.current?.select();
      setNotice('Copy the selected key using your keyboard.');
    }
  };
  const keys = data?.keys || [];
  const activeCount = keys.filter((key) => !key.revoked_at && (!key.expires_at || new Date(key.expires_at) > new Date())).length;
  const exampleUrl = (data?.application?.api_url || '').replace(/\/$/, '') + (data?.application?.example_path || '');
  const example = 'curl "' + exampleUrl + '" \\\n  -H "Authorization: Bearer YOUR_API_KEY" \\\n  -H "Accept: application/json"';

  return (
    <main className="aa-keys">
      <div className="aa-keys__shell">
        <header className="aa-keys__header">
          <div>
            <p className="aa-keys__eyebrow"><KeyRound size={16} aria-hidden="true" /> {appName} · Developer access</p>
            <h1>Auto Appy API keys</h1>
            <a href="/api/" style={{ color: "#147db5", fontWeight: 600 }}>API documentation ↗</a>
            <p>Connect external apps and automations to {appName} with a key that works without signing in each time.</p>
          </div>
          <button ref={createButton} className="aa-keys__primary" disabled={loading || busy || !data?.can_create || Boolean(created)}
            onClick={() => { setCreating(true); setNotice(''); }}><Plus size={17} /> Create API key</button>
        </header>

        <div className="aa-keys__info">
          <ShieldCheck size={22} aria-hidden="true" />
          <div><strong>One app. Your existing access.</strong><p>Each key belongs to your account and works only with {appName}. Your current organization and record permissions still apply. A key with no expiry works until you revoke it or lose access.</p></div>
        </div>
        {error && <div className="aa-keys__error" role="alert">{error}</div>}
        {notice && <p className="aa-keys__notice" role="status">{notice}</p>}

        {created && <section className="aa-keys__card aa-keys__secret" aria-label="New API key">
          <h2>Copy your key now</h2><p>This is the only time the full key is shown. Store it in your integration’s secret settings.</p>
          <label htmlFor="aa-key-secret">New {appName} API key</label>
          <div className="aa-keys__copy"><input id="aa-key-secret" ref={secretInput} readOnly value={created.secret} autoComplete="off" spellCheck="false" />
            <button onClick={copy}><Copy size={16} /> Copy key</button></div>
          <button className="aa-keys__primary" onClick={closeSecret}>I’ve saved my key</button>
        </section>}

        {creating && !created && <section className="aa-keys__card" aria-label="Create an API key">
          <h2>Create API key · {appName}</h2>
          <form onSubmit={submit}>
            <div className="aa-keys__fields">
              <label>Key name<input ref={nameInput} value={name} onChange={(event) => setName(event.target.value)} maxLength={100} required placeholder="e.g. Website contact sync" /></label>
              <label>Access<select value={permission} onChange={(event) => setPermission(event.target.value)}>
                <option value="read">Read only</option><option value="write">Read and write</option></select></label>
              <label>Expiration<select value={expiry} onChange={(event) => setExpiry(event.target.value)}>
                <option value="">Never expires</option><option value="30">After 30 days</option><option value="90">After 90 days</option><option value="365">After one year</option></select></label>
            </div>
            <p className="aa-keys__hint">{permission === 'read' ? 'Read only allows supported viewing and listing operations.' : 'Read and write allows creating, updating, deleting, and running actions within your existing permissions.'}</p>
            {uncertain && <p role="alert">The creation result is uncertain. Refresh the list and revoke any key whose secret you did not receive before creating another.</p>}
            <div className="aa-keys__actions"><button type="submit" className="aa-keys__primary" disabled={busy || uncertain || !name.trim()}>{busy ? 'Creating…' : 'Generate key'}</button>
              <button type="button" disabled={busy} onClick={() => { setCreating(false); createButton.current?.focus(); }}>Cancel</button></div>
          </form>
        </section>}

        <section className="aa-keys__card" aria-label="Your API keys">
          <div className="aa-keys__section-header"><div><h2>Your API keys</h2><p>{activeCount} active · {appName}</p></div>
            <button onClick={refresh} disabled={loading || busy}><RefreshCw size={16} /> Refresh</button></div>
          {loading ? <p role="status">Loading API keys…</p> : !data ? <p>Refresh to load your keys.</p> : keys.length === 0 ? <div className="aa-keys__empty"><KeyRound size={28} /><h3>No API keys yet</h3><p>Create a named key for each integration so you can revoke it independently.</p></div> :
            <div className="aa-keys__list">{keys.map((key) => {
              const state = key.revoked_at ? 'Revoked' : key.expires_at && new Date(key.expires_at) <= new Date() ? 'Expired' : 'Active';
              return <article className="aa-keys__row" key={key.id}>
                <div className="aa-keys__key-name"><strong>{key.name}</strong><code>{key.prefix}…</code></div>
                <div><span className={'aa-keys__badge aa-keys__badge--' + state.toLowerCase()}>{state}</span><p>{key.permission === 'write' ? 'Read and write' : 'Read only'}</p></div>
                <dl><div><dt>Created</dt><dd>{displayDate(key.created_at)}</dd></div><div><dt>Last used</dt><dd>{displayDate(key.last_used_at, 'Not used yet')}</dd></div><div><dt>Expires</dt><dd>{displayDate(key.expires_at)}</dd></div></dl>
                {!key.revoked_at && <button className="aa-keys__danger" disabled={busy} onClick={() => setRevoke(key)} aria-label={'Revoke ' + key.name}><Trash2 size={16} /> Revoke</button>}
              </article>;
            })}</div>}
          {data && !data.can_create && <p role="status">App access is unavailable. You can still revoke your existing keys.</p>}
        </section>

        {revoke && <section ref={revokeSection} tabIndex={-1} className="aa-keys__card aa-keys__confirm" aria-label="Confirm revocation">
          <h2>Revoke “{revoke.name}”?</h2><p>Integrations using this key will stop working. Create a replacement first if you need uninterrupted access.</p>
          <div className="aa-keys__actions"><button className="aa-keys__danger" onClick={revokeKey} disabled={busy}>{busy ? 'Revoking…' : 'Revoke key'}</button><button onClick={() => setRevoke(null)} disabled={busy}>Keep key</button></div>
        </section>}

        {data && <section className="aa-keys__card aa-keys__usage" aria-label="Using your API key">
          <h2>Use your key</h2><p>Send the key as a Bearer token to the {appName} API. Use a read and write key for create, update, delete, and action requests.</p>
          <pre><code>{example}</code></pre>
          <p className="aa-keys__hint">Use the same API paths and request bodies as the app. For organization requests, send <code>X-AutoAppy-Workspace</code> with your central organization ID and any resource IDs required by the endpoint. Authentication settings and key management require sign-in.</p>
        </section>}
      </div>
    </main>
  );
}
